/* eslint-disable dot-notation */
// ============================================================
// axios ↔ apiResponseCache 接線（取代 Service Worker）
// ------------------------------------------------------------
// 成功的白名單 GET 寫進 IndexedDB；之後 server 5xx / timeout / 離線時，從快取撈出來組成 synthetic
// axios response 回給呼叫端（呼叫端完全無感，跟 SW 從 cache 撈一樣），並把 punch-in-info 的 stale
// 狀態同步進 Redux（取代原本 SW 在 response 上加 X-Sw-Cache-Fallback header 的角色；該 header 在
// fallback response 仍保留一份作 DevTools 觀測點）。
//
// ============================================================

import { AxiosError, AxiosResponse } from 'axios';
import * as apiResponseCache from '@src/db/apiResponseCache';

// 延遲 require 避免循環依賴（store → reducers → services → api）
// eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
const getStore = () => require('@src/store').getClientStore?.();
// eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
const getSetPunchInStale = () => require('@src/store/actions/punchIn').setPunchInStale;

const PUNCH_IN_INFO_PATTERN = /^punch-in-info(\/|$|\?)/;

// 跟 SW 的 API_CACHE_ALLOWLIST 等價；差別是 axios config.url 是相對於 baseURL（.../api/）的路徑，所以 anchor 在開頭。
// shifts / school-announcements 收斂成 list-only（不含 sub-path），避免 detail 或 day_shifts/all-shifts 等首頁用不到的 endpoint 也被快取
const CACHEABLE_URL_PATTERNS: RegExp[] = [
	PUNCH_IN_INFO_PATTERN,
	/^shifts\/?(\?|$)/,
	/^user\/profile(\/|$|\?)/,
	/^school\/theme(\/|$|\?)/,
	/^school-announcements\/?(\?|$)/,
];

interface RequestConfigLike {
	method?: string;
	url?: string;
	params?: unknown;
	// Background machine context requests retain the originating account across logout/login.
	cacheAccountId?: string;
}

// config.url 可能帶或不帶前導 / 或 api/（也可能是絕對 URL），統一去掉再比對 / 當 cache key
function normalizeUrl(rawUrl: string | undefined): string {
	if (!rawUrl) return '';
	return rawUrl.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/?(api\/)?/, '');
}

// query 也納入 cache key（day_shifts / all-shifts / shift 帶日期參數）。axios 把 inline query 留在 url、物件 params 放 config.params，兩邊都要併進來。
// ⚠️ 假設 caller 把 query 傳在 config.params 而非 inline；白名單 endpoint 的 service 層皆滿足此前提
function cacheKeyUrl(config: RequestConfigLike): string {
	const base = normalizeUrl(config.url);
	const { params } = config;
	if (params && typeof params === 'object') {
		const record = params as Record<string, unknown>;
		const serialized = Object.keys(record)
			.filter(k => record[k] !== undefined && record[k] !== null)
			.sort()
			.map(k => `${k}=${String(record[k])}`)
			.join('&');
		if (serialized) return base.includes('?') ? `${base}&${serialized}` : `${base}?${serialized}`;
	}
	return base;
}

function isCacheableGet(config: RequestConfigLike | undefined): boolean {
	if (!config) return false;
	if ((config.method || '').toLowerCase() !== 'get') return false;
	const url = normalizeUrl(config.url);
	return CACHEABLE_URL_PATTERNS.some(re => re.test(url));
}

function isPunchInInfo(config: RequestConfigLike | undefined): boolean {
	return !!config && PUNCH_IN_INFO_PATTERN.test(normalizeUrl(config.url));
}

// 「server 不穩」而非 client 自己錯：5xx / timeout (ECONNABORTED) / Network Error（含真離線）。4xx（含 401）不算
function isRetryableServerFailure(error: AxiosError): boolean {
	if (error.response) return error.response.status >= 500;
	if (error.code === 'ECONNABORTED') return true; // timeout
	if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') return true; // 離線 / DNS / CORS
	return false; // 取消等其他狀況不 fallback
}

function currentAccountId(): string {
	try {
		const id = getStore()?.getState()?.user?.profile?.id;
		return id || apiResponseCache.ANON_ACCOUNT_ID;
	} catch (e) {
		return apiResponseCache.ANON_ACCOUNT_ID;
	}
}

function dispatchPunchInStale(stale: boolean): void {
	try {
		const store = getStore();
		if (store) store.dispatch(getSetPunchInStale()(stale));
	} catch (e) {
		// store 還沒 ready 等狀況忽略
	}
}

// ── 成功回應：白名單 GET 寫快取；punch-in-info 同步清掉 stale flag ──
export function onApiResponseSuccess(response: AxiosResponse): void {
	const config = response.config as RequestConfigLike;
	if (!isCacheableGet(config)) return;
	// fire-and-forget；putResponse 內部自己吞錯，寫快取失敗不影響呼叫端
	apiResponseCache.putResponse({
		accountId: config.cacheAccountId ?? currentAccountId(),
		url: cacheKeyUrl(config),
		status: response.status,
		headers: { ...(response.headers || {}) },
		data: response.data,
	});
	// 拿到 fresh 的 punch-in-info GET → 解除 UI 降級（punch-in-info 必在白名單裡，所以一定先過 isCacheableGet）
	if (isPunchInInfo(config) && (!config.cacheAccountId || config.cacheAccountId === currentAccountId())) {
		dispatchPunchInStale(false);
	}
}

// ── 失敗回應：白名單 GET + server 不穩時，嘗試用快取組 synthetic response ──
// 回傳：
//   - 命中 → resolve 一個 AxiosResponse（statusText 標記、headers 帶 x-sw-cache-fallback:1 供 debug）
//   - miss / 不適用 → 把原 error reject 出去（呼叫端維持原本的失敗行為）
//   - 不該由本模組處理（非白名單、非 server 不穩）→ 回 null，由呼叫端走原流程
//
// Lookup 順序：先真實 accountId、miss 時 fallback 到 __anon。
//   user/profile / school/theme 在 setUser 進 Redux 前就被快取，永遠落在 __anon；reload 時 profile.id
//   若已被別處 rehydrate，真實 bucket 查不到、得靠 fallback 補。跨帳號隔離由 logoutCall 清 __anon 切斷。
export function maybeServeFromCache(error: AxiosError): Promise<AxiosResponse> | null {
	const config = error.config as RequestConfigLike | undefined;
	if (!isCacheableGet(config) || !isRetryableServerFailure(error)) return null;

	const url = cacheKeyUrl(config as RequestConfigLike);
	const accountId = config?.cacheAccountId ?? currentAccountId();
	const lookup = accountId === apiResponseCache.ANON_ACCOUNT_ID ?
		apiResponseCache.getResponse(accountId, url) :
		apiResponseCache.getResponse(accountId, url)
			.then(cached => cached || apiResponseCache.getResponse(apiResponseCache.ANON_ACCOUNT_ID, url));

	return lookup
		.then(cached => {
			if (!cached) throw error; // miss → 維持原 error
			if (isPunchInInfo(config) && (!config?.cacheAccountId || config.cacheAccountId === currentAccountId())) {
				dispatchPunchInStale(true);
			}
			const synthetic: AxiosResponse = {
				data: cached.data,
				status: cached.status,
				statusText: 'OK (offline cache)',
				headers: { ...cached.headers, 'x-sw-cache-fallback': '1' },
				config: error.config!,
				request: (error as { request?: unknown }).request,
			};
			return synthetic;
		})
		.catch(() => { throw error; }); // getResponse 不會 throw，但保險：最終一律把原 error 丟出去
}
